import { Grid, InputLabel, makeStyles } from '@material-ui/core';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { NumberRangeWithYearPeriod, RangeOflong } from '../../../generated/model/models';
import { useDefaultValueWatcher } from '../../../hooks/useDefaultValueWatcher';
import { useOnChange } from '../../../hooks/useOnChange';
import { CriteriaOptionsProps } from '../../../pages/FinancialData/shared/FinancialDataContext/FinancialDataContext.types';
import YearRangePickerFilterInput from '../../Filters/YearRangePickerFilterInput/YearRangePickerFilterInput';
import InputDropdown from '../Dropdown';

interface OwnProps {
  allowedYearRange: RangeOflong;
  defaultState: NumberRangeWithYearPeriod;
  defaultOptions: CriteriaOptionsProps[];
  defaultValue?: string[];
  onChange: (values: NumberRangeWithYearPeriod) => void;
  active: boolean;
}

export function InputDateRangeComponentWithDropdown({
  allowedYearRange,
  onChange,
  defaultState,
  defaultOptions = YEAR_DROPDOWN_OPTIONS,
  defaultValue = [YEAR_DROPDOWN_OPTIONS[1].value],
  active,
}: OwnProps) {
  const classes = useStyles();
  const { t } = useTranslation();
  const [state, setState] = useState<NumberRangeWithYearPeriod>(defaultState);

  useDefaultValueWatcher({ defaultValue: defaultState, state, setState });
  useOnChange(onChange, state);

  return (
    <Grid container direction="column">
      <Grid container justify="space-between" alignItems="center" className={classes.dropdownRoot}>
        <Grid item className={classes.dropdownLabel}>
          <InputLabel>{t('companies:for')}</InputLabel>
        </Grid>
        <Grid item className={classes.dropdown}>
          <InputDropdown
            label=""
            defaultValue={defaultValue}
            options={defaultOptions}
            onChange={handleDropdownChange}
          />
        </Grid>
      </Grid>
      {!isHideYearRange(state.criteria) && (
        <YearRangePickerFilterInput
          active={active}
          defaultValue={{
            value: { from: state?.years?.from, to: state?.years?.to },
          }}
          defaultAllowedYearRange={{
            from: allowedYearRange.from!,
            to: allowedYearRange.to!,
          }}
          onChange={(range) => {
            setState({
              ...state,
              years: { from: range.value?.from, to: range.value?.to },
            });
          }}
        />
      )}
    </Grid>
  );

  function isHideYearRange(value?: NumberRangeWithYearPeriod.CriteriaEnum) {
    return (
      value === (LAST_YEAR as NumberRangeWithYearPeriod.CriteriaEnum) ||
      value === ('AVERAGE' as NumberRangeWithYearPeriod.CriteriaEnum)
    );
  }

  function handleDropdownChange(value: string) {
    const newState = {
      ...state,
      criteria: value as NumberRangeWithYearPeriod.CriteriaEnum,
    };

    if (isHideYearRange(value as NumberRangeWithYearPeriod.CriteriaEnum)) {
      delete newState.years;
    } else if (!state.years) {
      newState.years = allowedYearRange;
    }

    setState(newState);
  }
}

const useStyles = makeStyles((theme) => ({
  gridContainer: {
    alignItems: 'center',
    justifyContent: 'space-between',
    margin: '10px 0',
  },
  gridItem: {
    width: '50%',
  },
  gridLabel: {
    margin: theme.spacing(0),
    marginTop: theme.spacing(8),
    marginBottom: theme.spacing(4),
    textTransform: 'none',
  },
  dropdownRoot: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: theme.spacing(4),
  },
  dropdownLabel: {
    marginRight: theme.spacing(2),
  },
  dropdown: {
    width: '85%',
  },
}));

// Dropdown options type should be NumberRangeWithYearPeriod.CriteriaEnum but setting this enum throws error. Need to updated .babelrc with
// Namespace not marked type-only declare. Non-declarative namespaces are only supported experimentally in Babel. To enable and review caveats see: https://babeljs.io/docs/en/babel-plugin-transform-typescript
const LAST_YEAR = 'LAST';
export const YEAR_DROPDOWN_OPTIONS = [
  {
    value: 'ALL_YEARS',
    label: 'companies:options:allYears',
  },
  {
    value: 'ONE_YEAR',
    label: 'companies:options:atLeastOneYear',
  },
  {
    value: 'TWO_YEARS',
    label: 'companies:options:atLeastTwoYears',
  },
  {
    value: 'THREE_YEARS',
    label: 'companies:options:atLeastThreeYears',
  },
  {
    value: 'ALL_YEARS_STRICT',
    label: 'companies:options:allYearsStrict',
  },
  {
    value: 'AVERAGE',
    label: 'companies:options:averageOfYears',
  },
  {
    value: LAST_YEAR,
    label: 'companies:options:lastAvailableYear',
  },
];

export const YEAR_DROPDOWN_TWO_OPTIONS = [
  {
    value: 'ONE_YEAR',
    label: 'companies:options:atLeastOneYear',
  },
  {
    value: 'TWO_YEARS',
    label: 'companies:options:atLeastTwoYears',
  },
  {
    value: 'THREE_YEARS',
    label: 'companies:options:atLeastThreeYears',
  },
  {
    value: 'ALL_YEARS_STRICT',
    label: 'companies:options:allYearsStrict',
  },
];

export const YEAR_DROPDOWN_THREE_OPTIONS = [
  {
    value: 'ONE_YEAR',
    label: 'companies:options:atLeastOneYear',
  },
  {
    value: 'ALL_YEARS_STRICT',
    label: 'companies:options:allYearsStrict',
  },
];
